import { Injectable } from '@angular/core';
import { AppConfigService } from '@core/index';
import { Airport } from '@store/origins/types';
import { Searcher } from 'fast-fuzzy';

import { getFilteredCityValues } from '../utils/city-selector.utils';

// provide at the module level so theres one copy of the fuzzy search per booker, instead of per city-selector
@Injectable()
export class CitySelectorFuzzySearchService {
  /** Fuzzy searcher object */
  private fuzzySearcher: Searcher<Airport, any>;
  /** List of all airports available to filter on */
  private allAirports: Airport[];

  constructor(private config: AppConfigService) {}

  /** Convert string boolean to boolean, default to true if value not explicitly set to 'false' */
  private booleanStringCheckDefaultToTrue(val: any): boolean {
    return typeof val === 'string' && val.toLowerCase() === 'false'
      ? false // convert string 'false' to boolean false
      : true; // true, undefined, null, NaN, '', and anything else will default to true
  }

  /**
   * Initialize the fuzzy search object with list of airports
   * @param airports List of airports fuzzy search should filter on
   */
  initialize(airports: Airport[]) {
    if (this.fuzzySearcher) {
      return; // dont need to initialize twice
    }
    const configKeys = this.config.bookerFuzzySearch.keySelector?.split(
      ',',
    ) || ['name', 'code']; // default to property name and code of an Airport object

    this.allAirports = airports.slice(); // shallow copy
    this.fuzzySearcher = new Searcher(airports, {
      ignoreCase: this.booleanStringCheckDefaultToTrue(
        this.config.bookerFuzzySearch.ignoreCase,
      ),
      ignoreSymbols: this.booleanStringCheckDefaultToTrue(
        this.config.bookerFuzzySearch.ignoreSymbols,
      ),
      normalizeWhitespace: this.booleanStringCheckDefaultToTrue(
        this.config.bookerFuzzySearch.normalizeWhitespace,
      ),
      threshold: this.config.bookerFuzzySearch.threshold || 0.75,
      // keyselector run once for every entry in Searcher
      // each key returned must exist on the object
      keySelector: obj => {
        const keys = [];
        for (const key of configKeys) {
          if (obj[key.trim()]) {
            keys.push(obj[key.trim()]);
          }
        }
        return keys;
      },
    });
  }

  /**
   * Fuzzy search the airports passed to initialize for a string
   * @param query String to search for
   * @returns Airport array of matching results
   */
  search(query: string): Airport[] {
    // throw error if the searcher object hasn't been instantiated
    if (!this.fuzzySearcher) {
      throw new Error(
        'Please initialize the fuzzy search object before searching',
      );
    }

    return this.fuzzySearcher.search(query) as Airport[];
  }

  /**
   * Fuzzy search based on the query and then pass results to default filter
   * @param query String to search for
   * @returns Filtered airports array
   */
  fuzzyThenFilter(query: string): Airport[] {
    const fuzzyResult = this.search(query);

    if (fuzzyResult.length === 0) {
      return [];
    }

    const parsedName = fuzzyResult[0].name.split(',');
    // use first part of name to give fuzzy search more results to user
    // for now until better option for default search is provided
    return getFilteredCityValues(this.allAirports, parsedName[0]);
  }

  /**
   * Perform default search and then fuzzy only if first search had no results
   * @param query String to search for
   * @returns Filtered airports array
   */
  fuzzyOnNoResults(query: string): Airport[] {
    const results = getFilteredCityValues(this.allAirports, query);

    if (results.length > 0) {
      return results;
    }

    return this.fuzzyOnlyThenSort(query);
  }

  fuzzyOnlyThenSort(query: string): Airport[] {
    const results = this.search(query);

    // TODO: perform sorting on results to match default sorting order

    return results;
  }
}
